import { Menu, Typography, Icon } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Users, Settings, CreditCard, BarChart2 } from "react-feather";
import { MenuItem } from "@/components";
import { useAuth } from "@/features/auth";
import { NotificationsModal } from "../../../settings/components/notifications-modal/notifications-modal";
import { AccountDetailsItem } from "./components";

type Props = {
  anchorEl: HTMLElement | null;
  closeMenu: () => void;
};

const UserMenu = ({ anchorEl, closeMenu }: Props) => {
  const { logout, isLogoutPending, accountDetails, isUserPermitted } = useAuth();
  const { t } = useTranslation("coi-platform-translations");
  const navigate = useNavigate();
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const toggleOpenNotificationsModal = useCallback(() => {
    setNotificationsModalOpen((prev) => !prev);
  }, []);

  const open = Boolean(anchorEl);

  const isUserAuthorizedToViewSubscription = isUserPermitted(["SUBSCRIPTION_VIEW"]);
  const isBillingOptionVisible =
    isUserAuthorizedToViewSubscription && accountDetails?.capabilities?.isCustomerPortalLinkAvailable;

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={closeMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        MenuListProps={{ sx: { paddingTop: 0 } }}
        slotProps={{
          paper: {
            sx: {
              minWidth: "200px",
              maxWidth: "320px",
            },
          },
        }}
      >
        <AccountDetailsItem />
        {isBillingOptionVisible && (
          <MenuItem
            data-testid="billing-menu-option"
            divider
            onClick={() => {
              window.location.href = import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_LINK;
            }}
          >
            <Icon sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
              <CreditCard />
            </Icon>
            <Typography sx={{ position: "relative", left: "12px" }} variant="body2">
              {t("navigation.billing_link")}
            </Typography>
          </MenuItem>
        )}
        {isUserAuthorizedToViewSubscription && (
          <MenuItem data-testid="consents-menu-option" divider onClick={() => navigate({ to: "/monthly-consents" })}>
            <Icon sx={{ display: "flex", justifyContent: "center", marginRight: "12px" }}>
              <BarChart2 />
            </Icon>
            <Typography variant="body2">{t("navigation.monthly_consents_link")}</Typography>
          </MenuItem>
        )}
        <MenuItem data-testid="teammates-menu-option" divider onClick={() => navigate({ to: "/teammates" })}>
          <Icon sx={{ display: "flex", justifyContent: "center", marginRight: "12px" }}>
            <Users />
          </Icon>
          <Typography variant="body2">{t("navigation.teammates_link")}</Typography>
        </MenuItem>
        <MenuItem data-testid="settings-menu-option" divider onClick={toggleOpenNotificationsModal}>
          <Icon sx={{ display: "flex", justifyContent: "center", marginRight: "12px" }}>
            <Settings />
          </Icon>
          <Typography variant="body2">{t("navigation.settings_link")}</Typography>
        </MenuItem>
        <MenuItem data-testid="logout" onClick={() => logout()} disabled={isLogoutPending}>
          <Typography variant="body2">{t("navigation.logout_button")}</Typography>
        </MenuItem>
      </Menu>
      <NotificationsModal
        notificationsModalOpen={notificationsModalOpen}
        toggleNotificationsModal={toggleOpenNotificationsModal}
      />
    </>
  );
};

export default UserMenu;
